import CreateApi from '../../../../shared/Settings/appSettings';
import {
    Box, Stack, Typography,
    Button,
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { IOSSwitch } from '../../../../shared/Components/iosSwitch';
import { useAuth } from 'react-oidc-context';
import { useTBContext } from '../../../Contexts/useTBStates';
import { RoundButton } from '../../TabComponents';
import { DataGridPro, GRID_CHECKBOX_SELECTION_COL_DEF, GridToolbar, useGridApiRef } from '@mui/x-data-grid-pro';
import { CustomEditInputCell } from '../../../../shared/Components/CustomEditCell';
import { CustomRankEditInputCell } from '../../../../shared/Components/RankEditCell';
import { Add, Circle, Refresh } from '@mui/icons-material';
import { StyledIconButton } from '../../../../shared/Components/StyledIconButton';
import type { TbRegistration, CourseHoleInfo, EventScoreWithId } from '@moment-golf/types';
import { effectiveRoundBool } from '../../Functions/applyUniformStartHoleToViewEventData';
import {
    formatHoleSlotLabel,
    getShotgunSlotSortIndex,
    normalizeShotgunOrder,
    resolveRoundHoles,
    type ShotgunSlotOrder,
} from '../../Functions/shotgunTeeSheetUtils';

interface OverviewProps {
    appDims: any;
    compHeights: any;
}

interface RoundScoreRow {
    _id: string;
    playerName: string;
    division: string;
    flight: string;
    teeTime: Date;
    startHole: string;
    startHoleSort: number;
    [key: number]: number | undefined;
    thru: number;
    total: number;
    scoreToPar: number;
    confirmed: boolean;
    submitted: boolean;
    widthdrawn: boolean;
    disqualified: boolean;
    indPlayoffWin: boolean;
    teamPlayoffWin: boolean;
    finalRank: { gross: number, net: number } | number;
    finalTeamRank: { gross: number, net: number } | number;
}


let roundHoles: CourseHoleInfo[] = [];

export const AttestScoresPanel = ({ appDims, compHeights }: OverviewProps) => {
    const auth = useAuth();
    const api = CreateApi();
    const gridApiRef = useGridApiRef();

    const {
        factor, appTheme, editMode, roundValue, setRoundValue,
        viewEvent, viewEventData,
        originalScores, originalRegistrations, originalViewEventData,
    } = useTBContext();

    const [roundScoreData, setRoundScoreData] = useState<any[]>([]);
    const [selectionModel, setSelectionModel] = useState<string[]>([]);
    const [allowSave, setAllowSave] = useState(false);
    const [lastScoreQuery, setLastScoreQuery] = useState(new Date(Date.UTC(2000, 0, 1)));
    const [scoreFormatToggle, setScoreFormatToggle] = useState('gross');

    const gridRef = useRef<HTMLDivElement>(null);

    const isShotgunRound = Boolean(
        viewEvent && effectiveRoundBool(viewEvent.rounds[roundValue - 1]?.shotgunStart),
    );

    useEffect(() => {
        if (viewEvent) {
            const firstRound = viewEvent.rounds?.[0];
            const formatValue = firstRound?.scoreFormat?.toLowerCase?.() !== 'net' ? 'gross' : 'net';
            setScoreFormatToggle(formatValue);
        }
    }, [viewEvent]);

    useEffect(() => {
        if (viewEvent && viewEventData && viewEventData.scores &&
            viewEventData.scores.length > 0 && roundValue && roundValue > 0) {
            roundHoles = resolveRoundHoles(viewEvent, viewEventData, roundValue);
            setRoundScoreData(mapScoreToHole());
        }
    }, [roundValue, viewEventData.scores, scoreFormatToggle]);

    useEffect(() => {
        if (roundScoreData.length > 0 && JSON.stringify(originalScores.current) !== JSON.stringify(roundScoreData)) {
            setAllowSave(true);
        } else {
            setAllowSave(false);
        }
    }, [roundScoreData, originalScores.current]);

    const refreshScores = async () => {
        if (!viewEvent?._id) return;
        await api.get(`/tb/GetEventScores`, {
            params: {
                _id: viewEvent._id,
                referenceDate: lastScoreQuery
            }
        })
            .then((res: { data: EventScoreWithId[] }) => {
                const updatedScores = JSON.parse(JSON.stringify(viewEventData.scores));
                updatedScores.forEach((score: EventScoreWithId, index: number) => {
                    const matchingScore = res.data.find((s) => s._id === score._id);
                    if (matchingScore) {
                        updatedScores[index] = matchingScore;
                    }
                });

                setRoundScoreData(mapScoreToHole(updatedScores));
                viewEventData.scores = JSON.parse(JSON.stringify(updatedScores));
                originalViewEventData.current.scores = JSON.parse(JSON.stringify(updatedScores));
            })
            .catch((err: any) => {
                console.error('Error refreshing scores', err);
            });
        setLastScoreQuery(new Date());
    }

    function mapScoreToHole(scores?: EventScoreWithId[], registrations?: TbRegistration[], round?: number) {

        const internalRoundValue = round ?? roundValue;
        const scoreFormat = viewEvent?.rounds[internalRoundValue-1]?.scoreFormat?.toLowerCase?.() ?? '';
        const scoresToMap = scores ?? viewEventData.scores;
        const registrationsToMap = registrations ?? viewEventData.registrations;
        const isShotgun = effectiveRoundBool(viewEvent?.rounds?.[internalRoundValue - 1]?.shotgunStart);
        const viewEventDataForSort = registrations !== undefined && scores !== undefined
            ? { courseData: viewEventData.courseData ?? [] }
            : viewEventData;

        const roundScoreData: RoundScoreRow[] = [];

        scoresToMap.forEach((score: EventScoreWithId) => {

            const regsForScore = registrationsToMap.filter((reg) => reg.eventScoreID === score._id);
            const matchingReg = regsForScore[0];
            const teeSlot = matchingReg?.teeTimes?.find((t) => Number(t.round) === internalRoundValue)
                ?? matchingReg?.teeTimes?.[internalRoundValue - 1];
            const teeTimeStr = teeSlot?.teeTime;

            const rs = score.roundScores[internalRoundValue-1];
            const startingHole = rs?.startingHole ?? teeSlot?.startingHole;
            const shotgunOrderRaw = rs?.shotgunOrder ?? teeSlot?.shotgunOrder ?? null;
            const shotgunOrder: ShotgunSlotOrder = normalizeShotgunOrder(shotgunOrderRaw);

            const divisionVals = regsForScore
                .map((reg) => reg.division)
                .filter((d): d is string => typeof d === 'string' && d.trim() !== '');
            const flightVals = regsForScore
                .map((reg) => reg.flight)
                .filter((f): f is string => typeof f === 'string' && f.trim() !== '');
            const divisionLabel = Array.from(new Set(divisionVals)).join(' / ');
            const flightLabel = Array.from(new Set(flightVals)).join(' / ');

            const roundScore: RoundScoreRow = {
                _id: score._id ?? '',
                playerName: registrationsToMap
                    .filter((reg) => reg.eventScoreID === score._id)
                    .map((reg) => reg.playerName).join(' / '),
                division: divisionLabel,
                flight: flightLabel,
                teeTime: teeTimeStr ? new Date(teeTimeStr) : new Date(0),
                startHole: isShotgun && startingHole != null
                    ? formatHoleSlotLabel(Number(startingHole), shotgunOrder)
                    : '',
                startHoleSort: isShotgun && startingHole != null
                    ? getShotgunSlotSortIndex(
                        viewEvent,
                        viewEventDataForSort,
                        internalRoundValue,
                        Number(startingHole),
                        shotgunOrderRaw,
                    )
                    : 0,
                total: 0,
                thru: 0,
                scoreToPar: 0,
                confirmed: false,
                submitted: false,
                widthdrawn: false,
                disqualified: false,
                indPlayoffWin: false,
                teamPlayoffWin: false,
                finalRank: 0,
                finalTeamRank: 0
            };
            
            score.roundScores[internalRoundValue-1].holeScores.forEach((hole) => {
                roundScore[hole.holeNumber] = hole.playerScore;
                if (hole.playerScore > 0) {
                    roundScore.scoreToPar += hole.playerScore - (roundHoles[hole.holeNumber-1]?.par ?? 0)
                    - (scoreFormat !== 'gross' && scoreFormatToggle === 'net' ? (hole.playerHcpAdj ?? 0) : 0);
                    roundScore.thru += 1;
                }
            });

            const total = score.roundScores[internalRoundValue-1].holeScores.reduce((acc, hole) => acc + hole.playerScore, 0);

            roundScore.confirmed = rs.confirmed;
            roundScore.submitted = rs.submitted;
            roundScore.widthdrawn = rs.withdrawn ?? (rs as { widthdrawn?: boolean }).widthdrawn ?? false;
            roundScore.disqualified = rs.disqualified;
            roundScore.indPlayoffWin = score.indPlayoffWin;
            roundScore.teamPlayoffWin = score.teamPlayoffWin;
            // Ensure rank data is in the correct format
            if (typeof score.finalRank === 'number') {
                roundScore.finalRank = { gross: score.finalRank, net: score.finalRank };
            } else if (score.finalRank && typeof score.finalRank === 'object') {
                roundScore.finalRank = { 
                    gross: score.finalRank.gross || 0, 
                    net: score.finalRank.net || 0 
                };
            } else {
                roundScore.finalRank = { gross: 0, net: 0 };
            }
            
            if (typeof score.finalTeamRank === 'number') {
                roundScore.finalTeamRank = { gross: score.finalTeamRank, net: score.finalTeamRank };
            } else if (score.finalTeamRank && typeof score.finalTeamRank === 'object') {
                roundScore.finalTeamRank = { 
                    gross: score.finalTeamRank.gross || 0, 
                    net: score.finalTeamRank.net || 0 
                };
            } else {
                roundScore.finalTeamRank = { gross: 0, net: 0 };
            }

            roundScore.total = total;
            roundScoreData.push(roundScore);

        })

        roundScoreData.sort((a, b) => {
            if (isShotgun) {
                if (a.startHoleSort !== b.startHoleSort) return a.startHoleSort - b.startHoleSort;
                return a.playerName.localeCompare(b.playerName);
            }
            if (a.teeTime < b.teeTime) return -1;
            if (a.teeTime > b.teeTime) return 1;
            return a.playerName.localeCompare(b.playerName);
        });

        originalScores.current = JSON.parse(JSON.stringify(roundScoreData));
        return roundScoreData;
    }

    const createColumns = () => {
        //console.log('Creating Columns');

        const holes = roundHoles;

        const scoreFormat = viewEvent?.rounds[roundValue - 1]?.scoreFormat?.toLowerCase?.() ?? '';
        const showNetDots = scoreFormat === 'net' || scoreFormat === 'both';
        const isNetView = scoreFormat === 'net' || (scoreFormat === 'both' && scoreFormatToggle === 'net');

        const wrapCell = (params: any) => {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {params.value}
                </Box>
            )
        }

        const totalCell = (params: any) => {
            if (!isNetView) return wrapCell(params);
            const score = viewEventData.scores?.find((s: EventScoreWithId) => s._id === params.row._id);
            const netTotal = score?.roundScores?.[roundValue - 1]?.holeScores?.reduce(
                (acc: number, hole: { playerScore: number; playerHcpAdj?: number | null }) =>
                    acc + (hole.playerScore > 0 ? hole.playerScore - (hole.playerHcpAdj ?? 0) : 0),
                0
            ) ?? params.value;
            return wrapCell({ ...params, value: netTotal });
        }

        const thruCell = (params: any) => {

            const rowData = roundScoreData.find((row: any) => row._id === params.id);
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: rowData.confirmed && rowData.submitted && 
                    params.value === Number(viewEvent?.rounds[roundValue-1]?.totalHoles || 18) ? 'red' : 'black'
                }}>
                    {params.value === Number(viewEvent?.rounds[roundValue-1]?.totalHoles || 18) && rowData.confirmed && rowData.submitted ? 'F' : params.value}
                </Box>
            )
        }

        const toParCell = (params: any) => {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {params.value === 0 ? 'E' : params.value > 0 ? '+' + params.value : params.value}
                </Box>
            )
        }

        const rankCell = (params: any) => {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: params.value[scoreFormatToggle] === 0 ? 'transparent' : 'black'
                }}
                >
                    {params.value[scoreFormatToggle]}
                </Box>
            )
        }

        return (
            viewEventData.courseData && viewEventData.courseData.length > 0 &&
            [
                { field: '_id', headerName: 'ID', flex: 1, headerClassName: 'custom-header' },
                ...(isShotgunRound
                    ? [
                        {
                            field: 'startHoleSort',
                            headerName: 'Start Hole Sort',
                            flex: 1,
                            sortable: true,
                            editable: false,
                            headerClassName: 'custom-header',
                        },
                        {
                            field: 'startHole',
                            headerName: 'Start Hole',
                            flex: 1,
                            minWidth: 80,
                            editable: false,
                            sortable: true,
                            headerClassName: 'custom-header',
                            renderCell: (params: any) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {params.value}
                                </Box>
                            ),
                        },
                    ]
                    : [{
                        field: 'teeTime',
                        headerName: 'Tee Time',
                        flex: 1,
                        minWidth: 80,
                        type: 'date',
                        editable: false,
                        sortable: true,
                        headerClassName: 'custom-header',
                        renderCell: (params: any) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {new Date(params.value).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'UTC' })}
                            </Box>
                        ),
                    }]),
                { field: 'playerName', headerName: 'Player Name', flex: 2, minWidth: 100, sortable: true, filterable: true,
                    renderCell: (params: any) => {
                        return (
                            <Box sx={{ width: '100%', height: '100%', alignContent: 'center', justifyContent: 'center' }}>
                                <Typography
                                    sx={{ fontSize: 12*factor,
                                         fontWeight: 500, textAlign: 'left', whiteSpace: 'wrap' }}
                                    color='primary.main'
                                >
                                    {params.value}
                                </Typography>
                            </Box>
                        )
                    }
                 },
                { field: 'division', headerName: 'Division', flex: 1, minWidth: 100, sortable: true, filterable: true, editable: false, headerClassName: 'custom-header',
                    renderCell: (params: any) => (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '4px' }}>
                            <Typography sx={{ fontSize: 12*factor, textAlign: 'left', whiteSpace: 'wrap' }} color='primary.main'>
                                {params.value ?? ''}
                            </Typography>
                        </Box>
                    ),
                },
                { field: 'flight', headerName: 'Flight', flex: 1, minWidth: 100, sortable: true, filterable: true, editable: false, headerClassName: 'custom-header',
                    renderCell: (params: any) => (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '4px' }}>
                            <Typography sx={{ fontSize: 12*factor, textAlign: 'left', whiteSpace: 'wrap' }} color='primary.main'>
                                {params.value ?? ''}
                            </Typography>
                        </Box>
                    ),
                },
                ...Array.from({ length: Number(viewEvent?.rounds[roundValue-1]?.totalHoles || 18) }, (_, index) => index + 1).map((number) => ({
                    field: number.toString(), headerName: `${number} Par ${holes[number-1]?.par || 0}`, 
                    flex: 1, minWidth: 60, type: 'number' as const, editable: !isNetView, disableColumnMenu: true,
                    renderHeader: (params: any) => {
                        return (
                            <Stack direction="column" spacing={0} alignItems="center" justifyContent="center">
                                <Typography 
                                    sx={{ fontSize: 12*factor, alignContent: 'center', justifyContent: 'center', fontWeight: 500 }}
                                    color='primary.main'
                                >
                                    {number}
                                </Typography>
                                <Typography sx={{ fontSize: 12*factor, alignContent: 'center', justifyContent: 'center' }}>
                                    Par: {holes[number-1]?.par || 0}
                                </Typography>
                            </Stack>
                        );
                    },
                    renderCell: (params: any) => {
                        const score = viewEventData.scores?.find((s: EventScoreWithId) => s._id === params.row._id);
                        const holeScore = score?.roundScores?.[roundValue - 1]?.holeScores?.find(
                            (h: { holeNumber: number }) => h.holeNumber === number
                        );
                        const playerHcpAdj = holeScore?.playerHcpAdj ?? 0;
                        const rowGross = params.row[number] ?? params.row[String(number)];
                        const grossScore =
                            rowGross !== undefined && rowGross !== null && rowGross !== ''
                                ? Number(rowGross)
                                : holeScore?.playerScore ?? params.value ?? 0;
                        const displayScore = (grossScore > 0 && isNetView)
                            ? grossScore - playerHcpAdj
                            : grossScore;
                        const showStrokeIndicators = showNetDots && playerHcpAdj !== 0;

                        return (
                            <Box 
                                sx={{ 
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    position: 'relative',
                                }}
                            >
                                {showStrokeIndicators && (
                                    <Stack direction="row" sx={{
                                        alignItems: 'center', justifyContent: 'flex-start',
                                        gap: '3px', padding: 0, zIndex: 1,
                                        position: 'absolute',
                                        top: 2, left: 4,
                                    }}>
                                        {playerHcpAdj > 0
                                            ? Array.from({ length: playerHcpAdj }, (_, idx) => (
                                                <Circle key={idx} sx={{ fontSize: 7 * factor, color: 'primary.main' }} />
                                              ))
                                            : Array.from({ length: Math.abs(playerHcpAdj) }, (_, idx) => (
                                                <Add key={idx} sx={{ fontSize: 8 * factor, color: 'primary.main', fontWeight: 700 }} />
                                              ))}
                                    </Stack>
                                )}
                                <Typography sx={{ fontSize: 14*factor, alignContent: 'center', justifyContent: 'center', fontWeight: 400,
                                    width: displayScore === 0 && grossScore === 0 ? 30 : 22, height: displayScore === 0 && grossScore === 0 ? 30 : 22, 
                                    borderRadius: displayScore === 0 && grossScore === 0 ? '50%' : 
                                    displayScore < holes[number-1]?.par ? '50%' : '4px',
                                    backgroundColor: displayScore === 0 && grossScore === 0 ? 'secondary.main' : 
                                        displayScore < holes[number-1]?.par ? 'rgb(166, 221, 178)' :
                                        displayScore > holes[number-1]?.par ? 'rgb(240, 131, 131)' :
                                        'transparent',
                                    border: displayScore === 0 && grossScore === 0 ? '1px solid transparent' : 
                                    displayScore < holes[number-1]?.par - 1 ? '4px double rgb(20, 68, 24)' :
                                    displayScore > holes[number-1]?.par + 1 ? '4px double rgb(112, 18, 18)' :
                                    '4px solid transparent'
                                }}
                                >
                                    {grossScore === 0 ? '' : displayScore}</Typography>
                            </Box>
                        );
                    },
                    renderEditCell: (params: any) => {

                        return (
                            <CustomEditInputCell
                                {...params}
                            />
                        )
                    }
                })),
                { field: 'confirmed', headerName: 'Confirmed', flex: 1, minWidth: 80, type: 'boolean', editable: !isNetView, disableColumnMenu: true, headerClassName: 'custom-header' },
                { field: 'submitted', headerName: 'Submitted', flex: 1, minWidth: 80, type: 'boolean', editable: !isNetView, disableColumnMenu: true, headerClassName: 'custom-header' },
                { field: 'widthdrawn', headerName: 'WD', flex: 1, minWidth: 60, type: 'boolean', editable: !isNetView, disableColumnMenu: true, headerClassName: 'custom-header' },
                { field: 'disqualified', headerName: 'DQ', flex: 1, minWidth: 60, type: 'boolean', editable: !isNetView, disableColumnMenu: true, headerClassName: 'custom-header' },
                { field: 'indPlayoffWin', headerName: 'Ind Win', flex: 1, minWidth: 80, type: 'boolean', editable: !isNetView, disableColumnMenu: true, headerClassName: 'custom-header' },
                { field: 'teamPlayoffWin', headerName: 'Team Win', flex: 1, minWidth: 80, type: 'boolean', editable: !isNetView, disableColumnMenu: true, headerClassName: 'custom-header' },
                { field: 'finalRank', headerName: 'Rank', flex: 1, minWidth: 80, editable: true, headerClassName: 'custom-header', renderCell: rankCell,
                    renderEditCell: (params: any) => {
                        return (
                            <CustomRankEditInputCell
                                {...params}
                                scoreType={scoreFormatToggle}
                            />
                        )
                    }
                },
                { field: 'finalTeamRank', headerName: 'Team Rank', flex: 1, minWidth: 80, editable: true, headerClassName: 'custom-header', renderCell: rankCell,
                    renderEditCell: (params: any) => {
                        return (
                            <CustomRankEditInputCell
                                {...params}
                                scoreType={scoreFormatToggle}
                            />
                        )
                    }
                },
                { field: 'thru', headerName: 'Thru', flex: 1, minWidth: 60, type: 'number', sortable: true, headerClassName: 'custom-header', renderCell: thruCell },
                { field: 'total', headerName: 'Total', flex: 1, minWidth: 60, sortable: true, headerClassName: 'custom-header', renderCell: totalCell },
                { field: 'scoreToPar', headerName: 'To Par', flex: 1, minWidth: 60, type: 'string', sortable: true, headerClassName: 'custom-header', renderCell: toParCell },
            ]
        );
    }

    const handleScoreUpdate = async () => {

        const saveScores: any[] = [];
        const confirmSubmitScores: any[] = [];
        const updatedScores = JSON.parse(JSON.stringify(roundScoreData));

        if (JSON.stringify(originalScores.current) !== JSON.stringify(updatedScores)) {

            updatedScores.forEach((score: RoundScoreRow) => {
                    const originalScore = originalScores.current.find((s: unknown) => (s as RoundScoreRow)._id === score._id) as RoundScoreRow | undefined;
                    if (originalScore) {
                        Object.keys(score).map((key) => {
                            const keyStr = key as string;
                            const scoreRec = score as unknown as Record<string, unknown>;
                            const origRec = originalScore as unknown as Record<string, unknown>;
                            if (Number(keyStr) && scoreRec[keyStr] !== origRec[keyStr] && !['playerName', 'total', '_id', 'confirmed', 'submitted'].includes(keyStr)) {
                                saveScores.push({
                                    eventScoreID: score._id,
                                    roundIndex: roundValue-1,
                                    holeIndex: Number(keyStr)-1,
                                    playerScore: Number(scoreRec[keyStr])
                                });
                            }
                        });

                        if (originalScore.confirmed !== score.confirmed || originalScore.submitted !== score.submitted || 
                            originalScore.widthdrawn !== score.widthdrawn || originalScore.disqualified !== score.disqualified ||
                            originalScore.indPlayoffWin !== score.indPlayoffWin || originalScore.teamPlayoffWin !== score.teamPlayoffWin ||
                            JSON.stringify(originalScore.finalRank) !== JSON.stringify(score.finalRank) || 
                            JSON.stringify(originalScore.finalTeamRank) !== JSON.stringify(score.finalTeamRank)) {
                            const fr = typeof score.finalRank === 'object' && score.finalRank && 'gross' in score.finalRank ? score.finalRank : { gross: 0, net: 0 };
                            const ftr = typeof score.finalTeamRank === 'object' && score.finalTeamRank && 'gross' in score.finalTeamRank ? score.finalTeamRank : { gross: 0, net: 0 };
                            confirmSubmitScores.push({
                                eventScoreID: score._id,
                                roundIndex: roundValue-1,
                                confirmed: score.confirmed,
                                submitted: score.submitted,
                                widthdrawn: score.widthdrawn,
                                disqualified: score.disqualified,
                                indPlayoffWin: score.indPlayoffWin,
                                teamPlayoffWin: score.teamPlayoffWin,
                                finalRank: { gross: Number(fr.gross), net: Number(fr.net) },
                                finalTeamRank: { gross: Number(ftr.gross), net: Number(ftr.net) }
                            });
                        }
                    }
            });

            if (saveScores.length > 0) {
                api.post('/tb/UpdateScores', {
                    userID: auth.user?.profile.sub,
                    eventScores: saveScores
                })
                .then((res: any) => {
                    //console.log(res);
                    refreshScores();
                })
                .catch((err: any) => {
                    console.error(err);
                });
            }

            if (confirmSubmitScores.length > 0) {
                api.post('/tb/ConfirmSubmitScores', {
                    userID: auth.user?.profile.sub,
                    eventScores: confirmSubmitScores
                })
                .then((res: any) => {
                    //console.log(res);
                    //console.log('Last Query', lastScoreQuery);
                    refreshScores();
                })
                .catch((err: any) => {
                    console.error(err);
                });
            }

        }

        originalRegistrations.current = JSON.parse(JSON.stringify(viewEventData.registrations));
        originalScores.current = JSON.parse(JSON.stringify(roundScoreData));
        setAllowSave(false);
    }

    const dataGrid = (
        rowData: any,
        columns: any,
        options?: {
            gridKey?: string;
            defaultSortModel?: { field: string; sort: 'asc' | 'desc' }[];
            isShotgunRound?: boolean;
        },
    ) => {
        const shotgunGrid = options?.isShotgunRound ?? false;
        const groupPinField = shotgunGrid ? 'startHole' : 'teeTime';
        const hasDivisionData =
            Array.isArray(rowData) &&
            rowData.some(
                (r: any) => r && typeof r.division === 'string' && r.division.trim() !== '',
            );
        const hasFlightData =
            Array.isArray(rowData) &&
            rowData.some(
                (r: any) => r && typeof r.flight === 'string' && r.flight.trim() !== '',
            );

        return (
            <Box 
                ref={gridRef}
                sx={{ 
                    width: compHeights.appUsableWidth,
                    height: 'max-content',
                    maxHeight:compHeights.appUsableHeight - 130,
                    padding: '0px 10px',
                    justifyContent: 'center',
                    alignContent: 'center',
                    backgroundColor: 'sepia.background',
                }}
            >
            <DataGridPro
                key={options?.gridKey ?? 'score-grid'}
                apiRef={gridApiRef}
                rows={rowData}
                getRowId={(row) => row._id}
                columns={columns}
                
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            _id: false,
                            startHoleSort: false,
                            division: hasDivisionData,
                            flight: hasFlightData,
                            widthdrawn: false,
                            disqualified: false,
                            indPlayoffWin: false,
                            teamPlayoffWin: false,
                            finalRank: true,
                            finalTeamRank: true,
                        },
                    },
                    pinnedColumns: {
                        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, groupPinField, 'playerName'],
                        right: ['thru','total', 'scoreToPar']
                    },
                    sorting: options?.defaultSortModel
                        ? { sortModel: options.defaultSortModel }
                        : hasDivisionData
                            // Playoff grid: group by division for finalRank entry context.
                            ? { sortModel: [{ field: 'division', sort: 'asc' }, { field: 'scoreToPar', sort: 'asc' }] }
                            : undefined,
                }}
                checkboxSelection
                slots={{
                    toolbar: GridToolbar
                }}  
                editMode='row'
                onCellEditStart={(params) => {
                    //console.log('Cell Edit Start', params);
                    const cellElement = document.querySelector(`[data-id="${params.id}"]`);
                    if (cellElement) {
                        (cellElement as HTMLElement).style.backgroundColor = 'blue';
                    }
                }} 
                processRowUpdate={(params) => {
                
                    const updatedParams = { ...params };
                    let scoreToPar = 0;
                    let thru = 0;
                    
                    // Calculate the total by summing all numeric keys (hole numbers)
                    const newHoles = Object.keys(updatedParams)
                        .filter(key => !isNaN(Number(key)) && Number(key) > 0);

                    newHoles.forEach((hole: any) => {
                        
                        if(Number(params[hole]) > 0) {
                            scoreToPar += Number(params[hole]) - roundHoles[hole-1]?.par;
                            thru += 1;
                            updatedParams[hole] = Number(params[hole]);
                        }
                    })

                    updatedParams.total = newHoles.reduce((sum: any, hole: any) => sum + (Number(params[hole]) || 0), 0);
                    updatedParams.scoreToPar = scoreToPar;
                    updatedParams.thru = thru;
                    
                    const updatedRows = roundScoreData.map((row: any) => 
                        row._id === updatedParams._id ? updatedParams : row
                    );
                    
                    setRoundScoreData(updatedRows); // Save the updated rows
                    return updatedParams; // Return the updated row to the DataGrid
                }}
                onProcessRowUpdateError={(params) => {
                    //console.log('Error', params);
                
                }}
                onRowSelectionModelChange={(newSelectionModel) => {
                    setSelectionModel(newSelectionModel as string[]);
                }}
                sx={{ 
                    marginTop: 1, 
                    fontSize: 12*factor,
                    width: compHeights.appUsableWidth,
                    backgroundColor: 'sepia.background',
                    '& .MuiDataGrid-main': {
                        backgroundColor: 'sepia.background',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        backgroundColor: 'sepia.background',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                        backgroundColor: 'sepia.background',
                    },
                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: 'sepia.background',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    },
                    '& .MuiDataGrid-columnHeaderTitleContainer': {
                        justifyContent: 'center',
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 500,
                        fontSize: 12*factor,
                        color: 'primary.main',
                        textAlign: 'center',
                    },
                    "& .MuiDataGrid-iconButtonContainer": {
                        backgroundColor: 'transparent',
                        padding: 0,
                        margin: 0,
                        width: 15,
                        display: 'none'
                    },
                    "& .MuiDataGrid-sortIcon": {
                        width: 15,
                        height: 15,
                        color: 'red',
                        display: 'none'

                    },
                    "& .MuiDataGrid-cell": {
                        backgroundColor: 'sepia.background',
                        "&:hover": {
                            backgroundColor: 'rgba(235, 235, 235, 1)',
                        },
                    },
                    "& .MuiDataGrid-row": {
                        backgroundColor: 'sepia.background',
                        "&:hover": {
                            backgroundColor: 'rgba(235, 235, 235, 1)',
                        },
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                        backgroundColor: 'rgba(235, 235, 235, 1)',
                        "&:hover": {
                            backgroundColor: 'rgba(235, 235, 235, 1)',
                        },
                    },
                }}
            />
            </Box>
        )
    }


    if (!viewEvent) return null;

    return (
        <Stack id='tb-attest-scores-container' direction={'column'} spacing={0}
            sx={{ width: '100%', height: '100%', alignItems: 'center',
                overflow: 'hidden', overscrollBehavior: 'none' }}>
            <Stack direction='row' gap={'20px'} width={compHeights.appUsableWidth - 60} maxHeight={35}
                padding={'10px 40px'} alignItems='center' justifyContent='flex-end'>
                <Typography variant='h6' sx={{ flex: 1, textAlign: 'left', fontWeight: 500 }}>Attest Scores</Typography>
                {allowSave &&
                    <Button
                        onClick={handleScoreUpdate}
                        sx={{
                            width: 'max-content',
                            borderRadius: 2,
                            padding: 0.5,
                            justifyContent: 'flex-start',
                            border: '1px solid transparent',
                            '&:hover': { border: `1px solid ${appTheme.palette.primary.extraLight}` },
                            '&:active': { backgroundColor: 'transparent' },
                            '&:focus': { backgroundColor: 'transparent' },
                        }}
                    >Save Changes</Button>
                }
                <StyledIconButton
                    appTheme={appTheme}
                    label='Refresh Scores'
                    factor={factor}
                    color='primary'
                    icon={<Refresh />}
                    width={'max-content'}
                    height={40}
                    onClick={refreshScores}
                />
                <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Round</Typography>
                <Stack direction='row' spacing={0} padding={'0px 0px'} alignItems='center' justifyContent='flex-end'>
                    {Array.from({ length: Number(viewEvent.totalRounds) }, (_, index) => index + 1).map((number) => (
                        <RoundButton
                            key={`${roundValue}-${number}`}
                            number={number}
                            roundValue={roundValue}
                            editMode={editMode}
                            appTheme={appTheme}
                            factor={factor}
                            setRoundValue={setRoundValue}
                        />
                    ))}
                </Stack>
                {viewEvent?.rounds &&
                    viewEvent.rounds.length > 0 &&
                    roundValue &&
                    roundValue > 0 &&
                    viewEvent.rounds[roundValue - 1]?.scoreFormat.toLowerCase() === 'both' &&
                    <Stack direction="row" spacing={2} justifyContent='center' mb={0.5} alignItems={'center'}>
                        <Typography variant="subtitle2" sx={{ width: 'max-content', textAlign: 'center' }}>
                            Gross
                        </Typography>
                        <IOSSwitch
                            disabled={allowSave}
                            checked={scoreFormatToggle === 'net'}
                            onChange={() => {
                                setScoreFormatToggle(scoreFormatToggle === 'net' ? 'gross' : 'net');
                            }}
                        />
                        <Typography variant="subtitle2" sx={{ width: 'max-content', textAlign: 'center' }}>
                            Net
                        </Typography>
                    </Stack>
                }
            </Stack>
            {roundScoreData.length > 0 && viewEvent && roundHoles.length > 0 &&
                dataGrid(roundScoreData, createColumns(), {
                    gridKey: `attest-${viewEvent?._id ?? ''}-${roundValue}`,
                    isShotgunRound,
                    defaultSortModel: isShotgunRound
                        ? [{ field: 'startHoleSort', sort: 'asc' }]
                        : [{ field: 'teeTime', sort: 'asc' }],
                })
            }
        </Stack>
    );
};
